import React, { useEffect, useState } from 'react';
import api from '../api';

export default function BankSync() {
  const [accounts, setAccounts]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [syncing, setSyncing]       = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [message, setMessage]       = useState(null);
  const [dateFrom, setDateFrom]     = useState('');
  const [syncConfig, setSyncConfig] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [balance, setBalance] = useState(null);

  const load = async () => {
    try {
      const [statusRes, configRes] = await Promise.all([
        api.get('/bank/status'),
        api.get('/bank/sync-config'),
      ]);
      setAccounts(statusRes.data.accounts || []);
      setSyncConfig(configRes.data);
      if ((statusRes.data.accounts || []).length > 0) {
        api.get('/bank/balance').then(r => setBalance(r.data.balances?.[0] || null)).catch(() => {});
      }
    } catch {}
    setLoading(false);
  };

  useEffect(() => {
    load();
    // Détecter retour callback BoursoBank
    const params = new URLSearchParams(window.location.search);
    if (params.get('bank_connected')) {
      setMessage({ type: 'success', text: 'Compte BoursoBank connecté avec succès !' });
      window.history.replaceState({}, '', window.location.pathname);
      load();
    }
    if (params.get('bank_error')) {
      setMessage({ type: 'error', text: 'Erreur de connexion : ' + params.get('bank_error') });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const handleConnect = async () => {
    setConnecting(true);
    setMessage(null);
    try {
      const r = await api.post('/bank/connect');
      window.location.href = r.data.url;
    } catch (err) {
      setMessage({ type: 'error', text: err?.response?.data?.error || 'Erreur de connexion' });
      setConnecting(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setMessage(null);
    try {
      const body = dateFrom ? { date_from: dateFrom } : {};
      const r = await api.post('/bank/sync', body);
      const { inserted, uncategorized_merchants } = r.data;
      let text = `Synchronisation terminée — ${inserted} nouvelle${inserted !== 1 ? 's' : ''} transaction${inserted !== 1 ? 's' : ''}.`;
      if (uncategorized_merchants?.length > 0)
        text += ` ⚠️ Marchands sans catégorie : ${uncategorized_merchants.join(', ')}.`;
      setMessage({ type: 'success', text });
    } catch (err) {
      setMessage({ type: 'error', text: err?.response?.data?.error || 'Erreur de synchronisation' });
    }
    setSyncing(false);
  };

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    try {
      const r = await api.put('/bank/sync-config', syncConfig);
      setSyncConfig(r.data);
      setMessage({ type: 'success', text: 'Configuration enregistrée.' });
    } catch (err) {
      setMessage({ type: 'error', text: err?.response?.data?.error || 'Erreur de sauvegarde' });
    }
    setSavingConfig(false);
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Déconnecter le compte BoursoBank ?')) return;
    try {
      await api.delete('/bank/disconnect');
      setAccounts([]);
      setMessage({ type: 'success', text: 'Compte déconnecté.' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur lors de la déconnexion' });
    }
  };

  if (loading) return <div style={{ padding: 24, color: 'var(--text-muted)' }}>Chargement…</div>;

  return (
    <div style={{ maxWidth: 560, padding: '24px 0' }}>
      <h3 style={{ margin: '0 0 4px' }}>Synchronisation bancaire</h3>
      <p style={{ margin: '0 0 20px', color: 'var(--text-soft)', fontSize: 13 }}>
        Connecte ton compte BoursoBank via Open Banking (lecture seule).
      </p>

      {message && (
        <div style={{
          marginBottom: 16,
          padding: '10px 14px',
          borderRadius: 8,
          fontSize: 13,
          background: message.type === 'success' ? '#f0fdf4' : '#fef2f2',
          color:      message.type === 'success' ? '#166534' : '#991b1b',
          border:     `1px solid ${message.type === 'success' ? '#bbf7d0' : '#fecaca'}`,
        }}>
          {message.text}
        </div>
      )}

      {accounts.length === 0 ? (
        <div style={{
          border: '1px solid var(--border)',
          borderRadius: 12,
          padding: '24px',
          textAlign: 'center',
          color: 'var(--text-soft)',
          marginBottom: 16,
        }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>🏦</div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Aucun compte connecté</div>
          <div style={{ fontSize: 13, marginBottom: 20 }}>
            Connecte ton compte BoursoBank pour synchroniser automatiquement tes transactions.
          </div>
          <button
            onClick={handleConnect}
            disabled={connecting}
            style={{
              padding: '10px 24px',
              borderRadius: 8,
              background: connecting ? 'var(--border)' : 'var(--primary)',
              color: '#fff',
              border: 'none',
              fontWeight: 600,
              fontSize: 14,
              cursor: connecting ? 'wait' : 'pointer',
            }}
          >
            {connecting ? 'Redirection…' : 'Connecter BoursoBank'}
          </button>
        </div>
      ) : (
        <>
          {accounts.map(acc => (
            <div key={acc.id} style={{
              border: '1px solid var(--border)',
              borderRadius: 12,
              padding: '16px 20px',
              marginBottom: 12,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <div>
                <div style={{ fontWeight: 600, marginBottom: 2 }}>
                  {acc.account_name || 'Compte BoursoBank'}
                </div>
                {acc.iban && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                    {acc.iban.replace(/(.{4})/g, '$1 ').trim()}
                  </div>
                )}
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                  Expire le {new Date(acc.valid_until).toLocaleDateString('fr-FR')}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                {balance ? (
                  <>
                    <div style={{ marginBottom: 6 }}>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 1 }}>Comptabilisé</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: balance.clbd?.amount >= 0 ? 'var(--text)' : '#dc2626' }}>
                        {balance.clbd?.amount.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                      </div>
                    </div>
                    <div style={{ marginBottom: 6 }}>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 1 }}>Mouvements à venir</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: balance.xpcd?.amount >= 0 ? 'var(--text)' : '#dc2626' }}>
                        {balance.xpcd?.amount.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                      </div>
                    </div>
                    {balance.clbd?.date && (
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>
                        au {new Date(balance.clbd.date).toLocaleDateString('fr-FR')}
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Solde…</div>
                )}
                <span style={{
                  display: 'inline-block', marginTop: 2,
                  fontSize: 11, fontWeight: 700,
                  padding: '3px 8px', borderRadius: 99,
                  background: '#f0fdf4', color: '#166534',
                  border: '1px solid #bbf7d0',
                }}>
                  Connecté
                </span>
              </div>
            </div>
          ))}

          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: 'var(--text-soft)', display: 'block', marginBottom: 4 }}>
              Importer depuis le (laisser vide = automatique)
            </label>
            <input
              type="date"
              value={dateFrom}
              max={new Date().toISOString().slice(0, 10)}
              onChange={e => setDateFrom(e.target.value)}
              style={{
                padding: '8px 12px', borderRadius: 8,
                border: '1px solid var(--border)',
                fontSize: 13, color: 'var(--text)',
                background: 'var(--surface)',
              }}
            />
            {dateFrom && (
              <button
                onClick={() => setDateFrom('')}
                style={{ marginLeft: 8, background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 12 }}
              >
                ✕ réinitialiser
              </button>
            )}
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <button
              onClick={handleSync}
              disabled={syncing}
              style={{
                padding: '10px 20px',
                borderRadius: 8,
                background: syncing ? 'var(--border)' : 'var(--primary)',
                color: '#fff',
                border: 'none',
                fontWeight: 600,
                fontSize: 14,
                cursor: syncing ? 'wait' : 'pointer',
              }}
            >
              {syncing ? 'Synchronisation…' : '↻ Synchroniser maintenant'}
            </button>
            <button
              onClick={handleDisconnect}
              style={{
                padding: '10px 20px',
                borderRadius: 8,
                background: 'none',
                color: 'var(--danger)',
                border: '1px solid var(--danger)',
                fontWeight: 600,
                fontSize: 14,
                cursor: 'pointer',
              }}
            >
              Déconnecter
            </button>
          </div>

          {/* ── Sync automatique ── */}
          {syncConfig && (
            <div style={{
              marginTop: 24,
              padding: '16px 20px',
              border: '1px solid var(--border)',
              borderRadius: 12,
              background: 'var(--surface)',
            }}>
              <div style={{ fontWeight: 600, marginBottom: 14, fontSize: 14 }}>Sync automatique</div>

              {/* Toggle activé/désactivé */}
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, cursor: 'pointer' }}>
                <div
                  onClick={() => setSyncConfig(c => ({ ...c, enabled: !c.enabled }))}
                  style={{
                    width: 40, height: 22, borderRadius: 11,
                    background: syncConfig.enabled ? 'var(--primary)' : 'var(--border)',
                    position: 'relative', transition: 'background .2s', cursor: 'pointer',
                  }}
                >
                  <div style={{
                    position: 'absolute', top: 3, left: syncConfig.enabled ? 21 : 3,
                    width: 16, height: 16, borderRadius: '50%',
                    background: '#fff', transition: 'left .2s',
                    boxShadow: '0 1px 3px rgba(0,0,0,.2)',
                  }} />
                </div>
                <span style={{ fontSize: 13, color: 'var(--text-soft)' }}>
                  {syncConfig.enabled ? 'Activée' : 'Désactivée'}
                </span>
              </label>

              {/* Heure */}
              {syncConfig.enabled && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                  <span style={{ fontSize: 13, color: 'var(--text-soft)', minWidth: 80 }}>Heure :</span>
                  <select
                    value={syncConfig.hour}
                    onChange={e => setSyncConfig(c => ({ ...c, hour: Number(e.target.value) }))}
                    style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, background: 'var(--surface)', color: 'var(--text)' }}
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={i}>{String(i).padStart(2, '0')}h</option>
                    ))}
                  </select>
                  <select
                    value={syncConfig.minute}
                    onChange={e => setSyncConfig(c => ({ ...c, minute: Number(e.target.value) }))}
                    style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, background: 'var(--surface)', color: 'var(--text)' }}
                  >
                    {[0, 15, 30, 45].map(m => (
                      <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
                    ))}
                  </select>
                </div>
              )}

              <button
                onClick={handleSaveConfig}
                disabled={savingConfig}
                style={{
                  padding: '8px 18px', borderRadius: 8,
                  background: savingConfig ? 'var(--border)' : 'var(--primary)',
                  color: '#fff', border: 'none',
                  fontWeight: 600, fontSize: 13,
                  cursor: savingConfig ? 'wait' : 'pointer',
                }}
              >
                {savingConfig ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
